import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Copy, RotateCcw, Mail, ArrowUpRight, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PERSONALITIES } from "@/lib/quiz";
import { ShareCard } from "@/components/quiz/ShareCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const rise = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: EASE, delay },
});

const AreaBar = ({ area, delay }) => (
  <div data-testid={`quiz-area-${area.key}`}>
    <div className="flex items-baseline justify-between text-sm">
      <span className="font-semibold text-primary">{area.label}</span>
      <span className="font-display text-lg italic text-foreground/70">{area.pct}%</span>
    </div>
    <div className="mt-2 h-1 w-full bg-border">
      <motion.div
        className="h-1 bg-primary"
        initial={{ width: 0 }}
        animate={{ width: `${area.pct}%` }}
        transition={{ duration: 0.9, ease: EASE, delay }}
      />
    </div>
  </div>
);

const EmailResult = ({ resultId }) => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!email.trim() || !resultId) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/quiz/results/${resultId}/email`, { email: email.trim() });
      setSent(true);
      toast.success("Your result is on its way.");
    } catch {
      toast.error("Couldn't send the email. Please check the address.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <p className="flex items-center gap-2 text-sm text-primary" data-testid="quiz-email-sent">
        <Check size={16} /> Sent to {email}. Check your inbox (and spam folder).
      </p>
    );
  }

  return (
    <form onSubmit={send} className="flex flex-col gap-3 sm:flex-row" data-testid="quiz-email-form">
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        className="h-12 flex-1"
        data-testid="quiz-email-input"
        required
      />
      <button
        type="submit"
        disabled={busy || !resultId}
        data-testid="quiz-email-submit"
        className="inline-flex items-center justify-center gap-2 rounded-full border border-primary px-6 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground disabled:opacity-60"
      >
        <Mail size={16} /> {busy ? "Sending..." : "Email me my result"}
      </button>
    </form>
  );
};

const QuizResult = ({ result, resultId, onRestart }) => {
  const p = PERSONALITIES[result.personality_key];

  const copy = () => {
    const text =
      `My Money Passbook result\n\n` +
      `Money Personality: ${result.personality}\n` +
      `Financial Well-Being Check: ${result.score}/10\n\n` +
      `Strongest area: ${result.strongest_area}\n` +
      `Area to work on: ${result.weakest_area}\n` +
      `Next action: ${result.next_action}\n\n` +
      `Knowledge check: ${result.knowledge_correct}/3 correct.`;
    navigator.clipboard.writeText(text).then(() => toast.success("Result copied."));
  };

  return (
    <div data-testid="quiz-result">
      <motion.p {...rise(0)} className="text-xs font-bold uppercase tracking-[0.22em] text-accent">
        Final passbook entry
      </motion.p>

      <div className="mt-6 grid gap-8 md:grid-cols-[1.4fr_1fr] md:gap-12">
        <motion.div {...rise(0.1)}>
          <p className="text-sm text-foreground/60">Your Money Personality</p>
          <h1
            className="mt-2 font-display text-5xl font-medium leading-[0.98] tracking-tightest text-primary sm:text-6xl"
            data-testid="quiz-result-personality"
          >
            {result.personality}
          </h1>
          <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-foreground/80">{p.desc}</p>
        </motion.div>

        <motion.div
          {...rise(0.2)}
          className="grain relative overflow-hidden rounded-sm bg-primary p-8 text-primary-foreground"
        >
          <p className="relative z-[2] text-sm text-primary-foreground/70">Your Financial Well-Being Check</p>
          <p className="relative z-[2] mt-3 font-display text-7xl font-medium leading-none tracking-tightest" data-testid="quiz-result-score">
            {result.score}
            <span className="text-3xl text-primary-foreground/60">/10</span>
          </p>
          <p className="relative z-[2] mt-4 text-xs text-primary-foreground/60">
            Knowledge check: {result.knowledge_correct}/3 correct
          </p>
        </motion.div>
      </div>

      <motion.div {...rise(0.3)} className="mt-12 grid gap-6 border-t border-border pt-10 md:grid-cols-2 md:gap-10">
        <div className="grid gap-5">
          {result.areas.map((a, i) => (
            <AreaBar key={a.key} area={a} delay={0.4 + i * 0.08} />
          ))}
        </div>
        <div className="grid gap-5 text-base">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-foreground/55">Your strongest area</p>
            <p className="mt-1 font-display text-2xl text-primary" data-testid="quiz-result-strongest">
              {result.strongest_area}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-foreground/55">Area to work on</p>
            <p className="mt-1 font-display text-2xl text-primary" data-testid="quiz-result-weakest">
              {result.weakest_area}
            </p>
          </div>
          <div className="rounded-sm border border-secondary/60 bg-secondary/10 p-4">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent">Your next action</p>
            <p className="mt-1 text-base font-semibold text-primary" data-testid="quiz-result-action">
              {result.next_action}
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div {...rise(0.45)} className="mt-12 border-t border-border pt-10">
        <h3 className="font-display text-2xl text-primary">Where to go from here</h3>
        <ul className="mt-5 grid gap-4">
          {p.tips.map((t, i) => (
            <li key={t} className="flex gap-4 text-base text-foreground/80">
              <span className="font-display text-lg italic text-secondary">{String(i + 1).padStart(2, "0")}</span>
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </motion.div>

      <motion.div {...rise(0.5)} className="mt-12 rounded-sm border border-border bg-card p-6">
        <ShareCard result={result} />
      </motion.div>

      <motion.div {...rise(0.55)} className="mt-6 rounded-sm border border-border bg-card p-6">
        <p className="text-sm font-semibold text-primary">Want a copy? (optional)</p>
        <p className="mt-1 text-sm text-foreground/65">
          Your result is stored anonymously. Add an email only if you'd like it sent to you.
        </p>
        <div className="mt-4">
          <EmailResult resultId={resultId} />
        </div>
      </motion.div>

      <motion.div {...rise(0.65)} className="mt-10 flex flex-wrap gap-3">
        <a
          href="/#book"
          data-testid="quiz-book-btn"
          className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground"
        >
          Book a session to build on this
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </a>
        <button
          onClick={copy}
          data-testid="quiz-copy-btn"
          className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-semibold text-primary transition-colors hover:bg-muted"
        >
          <Copy size={16} /> Copy my result
        </button>
        <button
          onClick={onRestart}
          data-testid="quiz-restart-btn"
          className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-foreground/70 transition-colors hover:text-primary"
        >
          <RotateCcw size={16} /> Retake
        </button>
      </motion.div>

      <p className="mt-10 text-xs text-foreground/55">
        Educational only — this isn't financial advice. Twelve responses indicate tendencies, not a
        diagnosis; use this as a starting point for reflection.
      </p>
    </div>
  );
};

export default QuizResult;
